/**
 * THE single tuning file (HANDOFF §2 / §7).
 *
 * EVERY gameplay number lives here as a named, typed export. No magic numbers
 * may live anywhere else in `src/sim/**`. Values are taken from HANDOFF §3 and
 * the validated prototype (`fairway-clash-prototype.html`); each export cites its
 * source in a comment.
 *
 * UNSTABLE flags
 * --------------
 * Values HANDOFF marks "(unstable — see §5)" are part of the broken
 * landing → scatter → push → slope-roll → settle tuning loop. They are flagged
 * with `// UNSTABLE §5` (the bug) or `// §5b TUNE` (the post-fix difficulty
 * distribution target). Agent B re-derives every flagged value once the §5 bug
 * is fixed; the numbers here are the prototype's current (broken) values, kept
 * only so the sim is runnable and so the diff is legible. DO NOT trust them for
 * balance until Agent B signs off on §5 / §5b.
 *
 * Pure data only — no DOM, no randomness, no logic beyond `as const` literals.
 */

import type { CardFlight, CardShape, CardSpin, Difficulty, Rarity, TeeBoxKey } from './types.ts';

// ---------------------------------------------------------------------------
// Math constants (pure conversions — not tuning knobs)
// ---------------------------------------------------------------------------

/** Radians per degree. Club `spread`/`roughSpreadDegrees` are in degrees (§3.3/§3.7). */
export const DEG_TO_RAD = Math.PI / 180;
/** Quarter-turn (90°) in radians; the perpendicular used for the lateral pushOff (§3.4). */
export const HALF_PI = Math.PI / 2;
/**
 * Float-comparison guard. Makes the impact "within band" test (§3.4 / §5 hyp.2)
 * robust at the exact band edge, where `(impSweet+impBand) - impSweet - impBand`
 * lands ~1e-14 from zero instead of exactly zero. A clean strike must yield pushOff 0.
 */
export const EPSILON = 1e-9;

// ---------------------------------------------------------------------------
// Match clock (§3.5)
// ---------------------------------------------------------------------------

/** Match length in seconds. Prototype `t:180`; HANDOFF §3.5 "3:00". */
export const MATCH_DURATION_SECONDS = 180;

// ---------------------------------------------------------------------------
// Energy economy (§3.6)
// ---------------------------------------------------------------------------

/** Starting energy for both sides. Prototype `energy:5`; HANDOFF §3.6 "start 5". */
export const ENERGY_START = 5;
/** Energy cap. Prototype `maxE:10`; HANDOFF §3.6 "max 10". */
export const ENERGY_MAX = 10;
/** Energy regenerated per second (player). Prototype `+dt*0.5`; HANDOFF §3.6 "+0.5/sec". */
export const ENERGY_REGEN_PER_SECOND = 0.5;

// ---------------------------------------------------------------------------
// Field & green geometry (§3.1 / §3.2)
// ---------------------------------------------------------------------------

/**
 * Green geometry rules (§3.1 / §3.2). The field is laid out in a virtual portrait
 * space of {@link FIELD.width} × {@link FIELD.height}; the layout helper scales
 * these factors against it. Prototype: `r = min(W*0.16, 72)`, pin `r*0.35` from
 * centre at a random angle, greens hug the right at x = W*0.70, mirrored at
 * y = H*0.14 (rival/top) and y = H*0.86 (you/bottom).
 */
export const FIELD = {
  /** Reference portrait width (px). Prototype `#app` max-width 440. HANDOFF §8. */
  width: 440,
  /** Reference portrait height (px). Prototype `#app` max-height 900. */
  height: 900,
} as const;

export const GREEN = {
  /**
   * CONTAINER radius = min(width * radiusWidthFactor, radiusMax) — the invariant
   * bounding radius the procedural SHAPE fits inside (§3.5 / ./greens.ts) and that
   * scoring rings, tees, hazards and the §5b calibration all key off.
   *
   * BUMPED for procedural shapes (was `min(W*0.16,72)` ≈ 70.4px) to a slightly
   * bigger container so an elongated/lobed shape still reads as a real green; the
   * §5b radii were re-tuned against the new value (see SCORING below).
   */
  radiusWidthFactor: 0.2, // SHAPE bump (was 0.16) — bigger container for the fitted shape
  radiusMax: 92, // SHAPE bump (was 72) — cap rises with the container
  /** Pin sits this fraction of the radius from the green centre. Prototype `r*0.35`. */
  pinOffsetFactor: 0.35,
  /** Greens hug the right edge at this fraction of width. Prototype `W*0.70`. */
  centerXFactor: 0.7,
  /** Rival (target/top) green centre y. Prototype `H*0.14`. */
  rivalCenterYFactor: 0.14,
  /** Your (defend/bottom) green centre y. Prototype `H*0.86`. */
  youCenterYFactor: 0.86,
} as const;

/**
 * Procedural green SHAPE ranges (§3.5 / ./greens.ts). Each green is a rotated
 * ellipse (elongation `e` ∈ [elongationMin, elongationMax], rotation ∈ [0, π))
 * deformed by a low-order radial lobe (amplitude ∈ [0, lobeMax]). The LARGER
 * semi-axis fills the container (`radius`); the smaller is `radius / e`. e=1 ⇒
 * round; large e + rotation ⇒ long / wide / diagonal; lobe ⇒ oval/oblong/bean.
 * All draws via the injected rng (deterministic; §4.3).
 */
export const SHAPE = {
  /** Min elongation (1 = a perfect circle). */
  elongationMin: 1.0,
  /**
   * Max elongation (long/wide greens). semi-minor = radius / e. Tuned to 1.55
   * (from a first pass at 1.9): a strong, clearly-elongated mix (minor axis ≈
   * 0.645·radius) that still keeps enough green AREA that the skilled off-shape
   * rate (bogey+ ≈ 12%) stays in the §5b "small" band — verified via
   * `node tests/harness/mc.mjs`.
   */
  elongationMax: 1.55, // §5b TUNED (was 1.9) — area floor for bogey+ vs shape variety
  /** Max radial-lobe amplitude (organic/bean deformation of the ellipse edge). */
  lobeMax: 0.16, // §5b TUNED (was 0.22) — keep bean concavity from eating green area
  /** Boundary samples the renderer traces per green outline (smooth at this size). */
  outlineSegments: 64,
} as const;

/**
 * Procedural FAIRWAY shape ranges (§3.1 / ./greens.ts). The fairway is the strip
 * the MID & SHORT shots launch from, built with the SAME rotated-ellipse-plus-lobe
 * logic as greens but biased LONG so it reads as a strip running along the mid↔short
 * diagonal. ONE shape is drawn per match and point-reflected for the opponent, so the
 * two fairways are mirror-identical. The container radius is derived from the mid↔short
 * span at layout time (× `lengthPad`); `elongation` then sets the strip WIDTH
 * (= radius / e) and `axisJitter` leans it (the tees re-centre onto the tilted axis).
 */
export const FAIRWAY = {
  /** Major axis padding past the mid↔short span (× half-span) so it overruns both tees. */
  lengthPad: 1.5,
  /** Min strip elongation (major / minor). Higher ⇒ narrower fairway. */
  elongationMin: 2.0,
  /** Max strip elongation. */
  elongationMax: 3.0,
  /** Max radial-lobe amplitude (organic edge), same harmonic as greens. */
  lobeMax: 0.16,
  /** Random lean of the strip's long axis off the mid↔short diagonal (radians). */
  axisJitter: 0.22,
} as const;

/**
 * Procedural slope magnitude (§3.2): `slopeMag = SLOPE_MAG_BASE + r * SLOPE_MAG_RANGE`,
 * range 0.4 (gentle) .. 1.3 (severe), where r ∈ [0,1) from the rng. Slope ANGLE is a
 * uniform random angle drawn at green creation. Prototype `0.4 + Math.random()*0.9`.
 */
export const SLOPE_MAG_BASE = 0.4;
export const SLOPE_MAG_RANGE = 0.9;

// ---------------------------------------------------------------------------
// Tee boxes (§3.1)
// ---------------------------------------------------------------------------

/**
 * Three tee boxes per player fan from the green toward mid-field. Distance-to-target
 * ordering MUST hold: SHORT < MID < LONG (§3.1 — do NOT reintroduce reversed).
 *
 * Prototype `buildTeeBoxes`: box `w = min(W*0.17,66)`, `h = min(H*0.045,30)`;
 * `startY = green.y + toTarget*(green.r + h*0.9)` (the LONG end, just outside own green);
 * `stepY = min(H*0.095,72)`, `stepX = W*0.15`; rows `{long:0, mid:1, short:2}` step
 * from own green toward the target. Column: `green.x - W*0.12 - stepX*(2-n)`.
 */
export const TEE_BOX = {
  widthFactor: 0.17,
  widthMax: 66,
  heightFactor: 0.045,
  heightMax: 30,
  /**
   * Tee layout (playtest): the tees sit to the SIDE of the green (toward field
   * centre). The LONG tee anchors the BACK-LEFT corner — offset sideways by
   * `longSideFactor` and pushed BACK to the container's rear edge by
   * `longBackFactor` (× green radius), so it's the furthest from the target. MID &
   * SHORT step FORWARD toward the target (`forwardFactor`) and drift across
   * (`sideStepFactor`) — a left→right diagonal. `shortSpacingMult` widens the
   * mid→short gap. SHORT < MID < LONG distance-to-target still holds.
   */
  longSideFactor: 0.34, // LONG tee's sideways offset from the green (× field width) → back-left corner
  longBackFactor: 0.6, // LONG tee pushed back (× green radius); eased off the field edge so its (bigger) box clears the HUD
  forwardFactor: 0.13, // per-step forward toward the target (× field height)
  forwardMax: 120,
  sideStepFactor: 0.07, // per-step sideways drift toward centre (× field width)
  shortSpacingMult: 1.4, // mid→short gap multiplier (more space between them)
} as const;

/** Row index per tee key (§3.1): steps from own green (long=0) toward target (short=2). */
export const TEE_ROW_INDEX: Readonly<Record<TeeBoxKey, number>> = {
  long: 0,
  mid: 1,
  short: 2,
};

/** Iteration order, far→near the target. */
export const TEE_KEYS: readonly TeeBoxKey[] = ['long', 'mid', 'short'];

// ---------------------------------------------------------------------------
// Clubs — offensive cards (§3.3)
// ---------------------------------------------------------------------------

/** Plain-data shape for the offensive-card table; mirrors {@link Club} in types.ts. */
export interface ClubDef {
  readonly id: string;
  readonly name: string;
  readonly rarity: Rarity;
  readonly cost: number;
  readonly power: number;
  readonly accuracy: number;
  readonly control: number;
  readonly forgiveness: number;
  readonly spin: CardSpin;
  readonly flight: CardFlight;
  readonly shape: CardShape;
}

/**
 * The offensive-card roster (B-geometry card model — see club-card-model memory / Club
 * Lab). Every card launches from ONE point per side; POWER alone decides reach. Stats are
 * 1..5 reliability bars + flight character; CARD_MAP turns them into shot parameters.
 * Cards are SIDEGRADES (a stat budget) — no card is strictly best.
 */
export const CLUB_DEFS: readonly ClubDef[] = [
  { id: 'tour-iron', name: 'Tour Iron', rarity: 'common', cost: 2, power: 3, accuracy: 3, control: 3, forgiveness: 4, spin: 'mid', flight: 'mid', shape: 'straight' },
  { id: 'bomber', name: 'Bomber', rarity: 'rare', cost: 5, power: 5, accuracy: 2, control: 3, forgiveness: 1, spin: 'run', flight: 'low', shape: 'straight' },
  { id: 'blade', name: 'Blade', rarity: 'epic', cost: 4, power: 2, accuracy: 5, control: 4, forgiveness: 2, spin: 'stop', flight: 'mid', shape: 'straight' },
  { id: 'spinner', name: 'Spinner', rarity: 'common', cost: 3, power: 2, accuracy: 3, control: 2, forgiveness: 3, spin: 'stop', flight: 'high', shape: 'straight' },
  { id: 'runner', name: 'Runner', rarity: 'rare', cost: 3, power: 3, accuracy: 3, control: 3, forgiveness: 3, spin: 'run', flight: 'low', shape: 'straight' },
  { id: 'shaper', name: 'Shaper', rarity: 'rare', cost: 3, power: 3, accuracy: 3, control: 3, forgiveness: 3, spin: 'mid', flight: 'mid', shape: 'fade' },
  { id: 'hawk', name: 'The Hawk', rarity: 'legend', cost: 6, power: 5, accuracy: 3, control: 3, forgiveness: 1, spin: 'mid', flight: 'high', shape: 'straight' },
  // --- Specialty additions → 5 per distance (names placeholder; workshop later). Each is a
  //     SIDEGRADE with a situational hook (stop = carries water, high/fade/draw = beats a tree,
  //     low = cuts wind). POWER picks the tee: LONG P4-5, MID P3, SHORT P1-2. ---
  // LONG (P4-5)
  { id: 'albatross', name: 'Albatross', rarity: 'epic', cost: 4, power: 4, accuracy: 3, control: 4, forgiveness: 2, spin: 'stop', flight: 'high', shape: 'straight' },
  { id: 'boomerang', name: 'Boomerang', rarity: 'rare', cost: 4, power: 4, accuracy: 3, control: 3, forgiveness: 3, spin: 'mid', flight: 'mid', shape: 'draw' },
  { id: 'sniper', name: 'Sniper', rarity: 'epic', cost: 5, power: 4, accuracy: 5, control: 4, forgiveness: 2, spin: 'stop', flight: 'mid', shape: 'straight' },
  // MID (P3)
  { id: 'wedge', name: 'Wedge', rarity: 'common', cost: 3, power: 3, accuracy: 4, control: 3, forgiveness: 3, spin: 'stop', flight: 'high', shape: 'straight' },
  { id: 'curl', name: 'Curl', rarity: 'rare', cost: 3, power: 3, accuracy: 2, control: 3, forgiveness: 4, spin: 'mid', flight: 'mid', shape: 'draw' },
  // SHORT (P1-2)
  { id: 'flick', name: 'Flick', rarity: 'common', cost: 2, power: 2, accuracy: 3, control: 3, forgiveness: 4, spin: 'stop', flight: 'high', shape: 'fade' },
  { id: 'pitch', name: 'Pitch', rarity: 'common', cost: 2, power: 1, accuracy: 4, control: 3, forgiveness: 3, spin: 'stop', flight: 'mid', shape: 'straight' },
  { id: 'nipper', name: 'Nipper', rarity: 'rare', cost: 3, power: 2, accuracy: 3, control: 4, forgiveness: 2, spin: 'run', flight: 'low', shape: 'straight' },
] as const;

/**
 * Per-tee-box shot variance (§3.3 playtest). Scales how a player's TIMING error maps
 * to the shot outcome, by launch box:
 *  - `distance` scales the power DEVIATION from a sweet swing (1 = baseline). LONG is
 *    forgiving (<1: easy to hit the distance); SHORT is punishing (>1: hard distance).
 *  - `lateral` scales the LINE scatter — base spread AND a mis-timed push (1 = baseline).
 *    LONG + SHORT are harder to keep straight (>1); MID is the even, baseline box.
 * A SWEET, CLEAN strike has zero power deviation + zero push, so this NEVER changes a
 * perfect shot (§5) — only how a MISS scales. Net over the distribution: short lands
 * closer (more birdie-or-better), long sprays from distance (fewer), mid in between.
 */
export const BOX_VARIANCE: Readonly<Record<TeeBoxKey, { distance: number; lateral: number }>> = {
  long: { distance: 0.6, lateral: 1.4 },
  mid: { distance: 0.78, lateral: 1.0 },
  short: { distance: 0.9, lateral: 1.1 },
};

/**
 * SET maximum shot reach per tee box (§3.3 playtest — Golf-Clash dial-it-in). A locked
 * backswing of fraction `bv` flies `bv × SHOT_RANGE[box] × club.carry`; FULL power
 * (bv = 1) reaches this distance. The pin sits at fraction `h = pinDist / range` on the
 * aim line, so the player TIMES the power to the hole instead of a fixed sweet spot — a
 * sweet swing no longer auto-finds the pin. LONG reaches farthest (but least precise on
 * distance, since the same timing error scales by the bigger range); SHORT is shortest
 * but most precise. Field-space px; tuned so the harness pins land at h ≈ 0.7.
 */
export const SHOT_RANGE: Readonly<Record<TeeBoxKey, number>> = {
  long: 810,
  mid: 660,
  short: 515,
};

/**
 * Per-tee SCORING ZONE scale (playtest). A LONG shot (from the back tee) gets the LARGEST
 * eagle / birdie / par / bogey zones — most forgiving; a SHORT approach gets the SMALLEST —
 * you have to be precise. MID = 1.0 (the §5b baseline). Scales the close-to-pin rings AND
 * the par/bogey green-shape tests in scoreShot. Tuned with §5b.
 */
export const ZONE_SCALE: Readonly<Record<TeeBoxKey, number>> = {
  long: 1.3,
  mid: 1.0,
  short: 0.78,
};

// ===========================================================================
// CARD MODEL → SHOT PARAMETERS (B geometry — ported from the Club Lab)
// ===========================================================================
// One launch point per side; per-card POWER decides reach. `range` is calibrated to the
// live launch→pin distance span (geometry step); the rest are stat curves.

const cardLerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const cardNorm = (stat: number): number => (stat - 1) / 4; // 1..5 → 0..1

/** Stat → shot-parameter mappings (ported from the Club Lab). */
export const CARD_MAP = {
  /** POWER → max reach (field px). CALIBRATED to the live launch→pin span (geometry step). */
  range: (power: number): number => cardLerp(520, 820, cardNorm(power)),
  /** ACCURACY → impact (direction) sweet-spot WIDTH multiplier on SWING.impBand. High accuracy
   *  = a wider clean zone, so it is EASIER to strike dead straight; a tap beyond it pushes the
   *  ball off-line (scaled by FORGIVENESS). A clean strike has ZERO wobble — perfect aces. */
  impactBand: (accuracy: number): number => cardLerp(0.55, 1.6, cardNorm(accuracy)),
  /** ACCURACY → the AI's per-club aim spread (degrees). The AI has no meter, so it sprays by
   *  this; the human's clean strike does NOT (see impactBand). */
  spreadDeg: (accuracy: number): number => cardLerp(2.45, 0.27, cardNorm(accuracy)),
  /** CONTROL → distance-deviation scale; high control = a power mistime strays less. */
  distanceVar: (control: number): number => cardLerp(1.5, 0.55, cardNorm(control)),
  /** FORGIVENESS → lateral push scale on a mis-timed impact; high = gentle. */
  pushScale: (forgiveness: number): number => cardLerp(1.7, 0.5, cardNorm(forgiveness)),
  /** SPIN → air-carry share (stop holds the green, run releases + rolls). */
  carryFraction: (spin: CardSpin): number => (spin === 'stop' ? 0.93 : spin === 'run' ? 0.7 : 0.83),
  /** SPIN → roll-out multiplier on an OVERSHOOT (run releases past the pin, stop checks up). */
  runMult: (spin: CardSpin): number => (spin === 'run' ? 1.3 : spin === 'stop' ? 0.72 : 1.0),
  /** SHAPE → deterministic mid-air bend (the Fade/Draw); straight flies dead. */
  shapeCurve: (shape: CardShape): number => (shape === 'fade' ? 0.5 : shape === 'draw' ? -0.5 : 0),
  /** FLIGHT → wind susceptibility (situational §3.7 counter): a LOW runner CUTS the wind (<1),
   *  a HIGH ball gets TOSSED (>1), mid is neutral. Scales the per-frame wind push in flight. */
  windFactor: (flight: CardFlight): number => (flight === 'high' ? 1.4 : flight === 'low' ? 0.6 : 1.0),
} as const;

/**
 * Situational flight-trait counters (the offense↔defense rock-paper-scissors, ported from the
 * Club Lab matchup grid). PURE trait gates — robust to geometry, easy to read for the player:
 *  - WATER carry: a front water hazard on the shot line must be flown — only a STOP-spin (high,
 *    soft-landing) club holds it; everything else (mid/run) splashes (a `double`). [Blade, Spinner]
 *  - TREE block: a tree on the line is cleared by HIGH flight (over) or a SHAPED fade/draw (around);
 *    a LOW/MID STRAIGHT club is blocked → a knockdown scramble (`bogey`). [Spinner, Shaper, Hawk]
 *  - WIND: see {@link CARD_MAP.windFactor} — LOW flight cuts it, HIGH gets tossed. [Bomber, Runner]
 */
export function carriesWater(spin: CardSpin): boolean {
  return spin === 'stop';
}
export function clearsTree(flight: CardFlight, shape: CardShape): boolean {
  return flight === 'high' || shape !== 'straight';
}

/**
 * Tree obstacle (§3.7 defensive card). A placed tree sits in the rough just in FRONT of the
 * defended green (toward the incoming shots) and BLOCKS any shot whose aim line passes within
 * `blockRadius` of its trunk — unless the club clears it (see {@link clearsTree}). Unlike a
 * catch-hazard it is never a rest-in-disc double; the block is resolved in flight (a `bogey`).
 */
export const TREE = {
  /** Base container/visual radius (px) before the per-hazard size jitter. */
  radius: 20,
  /** A shot line within this of the trunk (px) is blocked unless the club flies high / shapes. */
  blockRadius: 30,
} as const;

/**
 * POWER → which TEE the card launches from (the player's choice of shot, by card). High
 * power is a LONG shot from the back tee (more distance to cover → bigger error); low power
 * is a SHORT approach from the forward tee. The tee's SHOT_RANGE then gives the reach, so a
 * long shot naturally carries the long/mid/short distance personality.
 */
export function boxForPower(power: number): TeeBoxKey {
  return power >= 4 ? 'long' : power === 3 ? 'mid' : 'short';
}

/** Distance to the pin (px) the POWER meter's blue zone represents: tap inside it and a clean
 *  strike's carry lands within ~this of the pin (so "hit the zone" = reach near the pin). */
const POWER_BAND_DP = 30;

/**
 * POWER (distance) meter band HALF-width for a card (meter units). Derived from the REAL
 * distance sensitivity — the tee's BOX_VARIANCE.distance × the card's CONTROL × the tee range —
 * so the displayed blue zone matches what actually reaches. (The port had used CONTROL alone,
 * which drew the zone far too wide, so tapping its edge fell short of the green.)
 */
export function powerBandHalf(power: number, control: number): number {
  const box = boxForPower(power);
  const dvRange = BOX_VARIANCE[box].distance * CARD_MAP.distanceVar(control) * SHOT_RANGE[box];
  return Math.min(0.4, POWER_BAND_DP / dvRange);
}

// ---------------------------------------------------------------------------
// Two-stage swing (§3.4)
// ---------------------------------------------------------------------------

export const SWING = {
  /** Backswing (POWER) sweet centre. Prototype `backSweet = 0.80`. */
  backSweet: 0.8,
  /** Backswing half-band — the green DISTANCE zone. Tightened (was 0.114) to offset the
   *  slightly bigger birdie/eagle rings, keeping net difficulty. Below ⇒ short, above ⇒ long. */
  backBand: 0.105,
  /** Impact (ACCURACY) sweet centre. Prototype `impSweet = 0.50`. */
  impSweet: 0.5,
  /** Impact half-band (the tight gold zone). Tightened (was 0.05) to offset the bigger rings,
   *  but only slightly — lateral misses drive doubles, so over-tightening blows the bogey+ band. */
  impBand: 0.049,
  /**
   * Power mapping: `power = lockedMeter * (POWER_REFERENCE / backSweet)` so a sweet
   * backswing maps to POWER_REFERENCE. Prototype `power = a.power*(0.78/backSweet)`.
   */
  powerReference: 0.78,
  /** Off-sweet distance penalty: `power *= (1 - min(powerPenaltyMax, backOff*powerPenaltyGain))`. */
  powerPenaltyMax: 0.18,
  powerPenaltyGain: 0.8,
  /** Final power clamp. Prototype `max(0.30, min(1.05, power))`. */
  powerMin: 0.3,
  powerMax: 1.05,
  /** Lateral push: `pushOff = clamp(impMiss * pushOffGain, -pushOffMax, +pushOffMax)` px. */
  // §5 FIX: pushOff is the lateral consequence of a MIS-timed impact only. The §5
  // bug was that this lateral offset leaked onto clean strikes (hypothesis 2) and,
  // at full magnitude, threw a near-clean shot up to ~110px off the pin (matching
  // the reported ~100–112px symptom). The fix that actually matters is the
  // invariant `impMiss === 0 ⇒ pushOff === 0`, enforced in resolveSwing/createBall.
  // The gain/cap themselves stay at the prototype's tuned values (a clean strike
  // never reaches this code path), so mistimed shots still scatter realistically
  // and the §5b distribution tuning has the same lever to work with later.
  // Playtest: a mis-timed impact must DRAMATICALLY miss left/right. The lateral push
  // ramps in TWO stages so a SLIGHT miss stays near the green (skilled play / §5b is
  // preserved) but a CLEAR miss flies past the green container (radius ≈ 88px):
  //   push = pushOffGain·min(impMiss, knee) + pushOffSteepGain·max(0, impMiss − knee)
  // impMiss 0.05 → ~15px (on green); 0.15 → ~136px (off the container); 0.20+ → cap.
  pushOffKnee: 0.07, // forgiving margin past the yellow band edge (covers the skilled tail)
  pushOffGain: 600, // inner slope (≤ knee): even a slight mis-time clears the shot noise (~30px+)
  pushOffSteepGain: 1500, // steep outer slope (> knee) — a clear miss leaves the green
  pushOffMax: 220, // extreme mis-times land ~130px beyond the container edge
  /** PEAK meter sweep speed at the backswing sweet spot (units/sec). Feel knob. */
  backswingSpeed: 1.35,
  /** PEAK meter sweep speed at the impact sweet spot (faster = tighter). Feel knob. */
  impactSpeed: 1.8,
  /**
   * Meter velocity profile (playtest): the marker moves at PEAK speed through the
   * active sweet spot and eases to `meterSlowFactor × peak` at the extremes (0 and
   * 1), blended with smoothstep so it never stutters. Applied per-phase to that
   * phase's own sweet spot. 0 < meterSlowFactor < 1.
   */
  meterSlowFactor: 0.34,
} as const;

// ---------------------------------------------------------------------------
// Ball flight & post-flight displacement (§3.2 / §3.4 / §5) — UNSTABLE
// ---------------------------------------------------------------------------

/**
 * Flight + landing + roll parameters. The §5 bug ("perfect shot lands ~100px off")
 * lives in this block: the landing-scatter + pushOff + slope-roll combination
 * throws a clean strike off the pin. Agent B OWNS re-deriving these so a perfect
 * shot stays inside the birdie ring (roll total ≈ M*slopeMag*(N+1)/2; aim for
 * max-slope ≈ 12–18px, gentle ≈ 3–5px — HANDOFF §5 hypothesis 1).
 *
 * Prototype values are kept verbatim and flagged; none are trustworthy yet.
 */
export const FLIGHT = {
  /** In-flight step: `spd = max(speedFloor, remaining * speedDecay)`. Prototype `max(1.4, remain*0.06)`. */
  speedFloor: 1.4,
  speedDecay: 0.06,
  /** Distance within which the ball is "landed". Prototype `remain <= 1.2`. */
  landThreshold: 1.2,
  /** Curve applied per flight step for curving clubs (the Fade). Prototype `b.curve*0.006`. */
  curvePerStep: 0.006,
  /** Fraction of the shot the ball CARRIES in the air; the rest is ground roll-out.
   *  Raised so a WELL-STRUCK shot flies onto the GREEN and releases a short roll to
   *  the pin (rather than landing short in the rough and rolling a long way on). This
   *  is what lets {@link ROUGH_ROLL} damp ONLY shots that come down in the rough while
   *  a perfect shot — which now lands on the green — keeps its full roll (§5 intact). */
  carryFraction: 0.93,
} as const;

/**
 * Surface-dependent roll-out (§3.2 playtest): a ball that LANDS ON THE GREEN (or its
 * receptive fringe) releases its full roll-out; one that LANDS IN THE ROUGH grabs — it
 * rolls a SHORTER distance (`rollFactor`) and SLOWER (`frames` > {@link ROLL.frames}).
 * Keyed off the TOUCHDOWN point, so a perfect shot (lands on the green) is unaffected.
 */
export const ROUGH_ROLL = {
  rollFactor: 0.5, // forward roll-out distance scale for a rough landing (< 1 = rolls less)
  frames: 40, // roll-out duration for a rough landing (> ROLL.frames ⇒ a slower, grabbier roll)
  greenFringe: 30, // px past the green edge still treated as receptive (the short approach/fringe)
} as const;

/**
 * Slope roll on landing (§3.2). UNSTABLE §5 — prime §5 suspect.
 * Prototype: 16 frames, eased `t = framesLeft/16`, displacement per frame
 * `slopeX * 1.3 * t, slopeY * 1.3 * t`.
 */
export const ROLL = {
  // §5 FIX: roll total has the closed form  total = M * slopeMag * (N+1)/2
  // (eased displacement Σ_{f=1..N} M*slopeMag*(f/N) = M*slopeMag*(N+1)/2).
  // Target (HANDOFF §5 hyp.1): max-slope (slopeMag=1.3) ≈ 12–18px, gentle (0.4) ≈ 3–5px.
  // With N=16, M=1.3:  max = 1.3*1.3*8.5 = 14.4px,  gentle = 1.3*0.4*8.5 = 4.4px,
  // average (0.85) = 9.4px — all squarely inside the target bands, and well inside
  // birdieR (≈36.6px) even when stacked on the ≤8px clean scatter. The prototype's
  // own N/M already satisfy the derivation; they are KEPT (the §5 break never came
  // from roll magnitude — it came from the pushOff leak above), now with the
  // derivation documented and asserted by the headless harness.
  // `frames` is the ROLL-OUT duration (carry + roll-out model): the ball rolls its
  // ground roll-out + slope break eased over this many frames. `breakFrames` is the
  // CALIBRATED slope-break weight (kept at 16 so the §5 break magnitude is unchanged
  // regardless of roll duration): break total = M * slopeMag * (breakFrames+1)/2.
  frames: 26, // roll-out duration (longer = a slower, more drawn-out roll); break magnitude is separate
  breakFrames: 16, // §5 FIX: N=16 ⇒ (N+1)/2 = 8.5; the slope-break magnitude weight (unchanged)
  magnitude: 1.3, // §5 FIX: M=1.3 ⇒ max-slope roll 14.4px (in 12–18px band), gentle 4.4px
} as const;

/**
 * Landing scatter (§3.4 / §5). UNSTABLE §5 — even a clean strike currently gets
 * a scatter offset; §5 hypothesis 3 budgets pure-strike total displacement ≤ ~8px.
 * Prototype passes `scatter = 6` for player shots, default `34`; AI uses `9`.
 */
export const SCATTER = {
  // §5 FIX: scatter is the ONLY post-flight displacement a clean strike receives
  // (pushOff is 0 by invariant; roll is the readable break, separate). Budget
  // (HANDOFF §5 hyp.3): clean-strike scatter+push ≤ ~8px BEFORE the break-roll.
  // Drawn as sqrt(rng())*radius over the disk, so `player`=6 caps the clean-strike
  // displacement at 6px (mean ≈4px) — inside the 8px budget with push=0. createBall
  // uses `player` for every offense shot; `ai` (9) and `defaultRadius` (34) are
  // retained ONLY as named knobs for §5b distribution tuning / future AI-only paths,
  // and are deliberately NOT used by the clean human shot pipeline.
  player: 0, // a CLEAN strike has NO scatter — perfect meter timing lands exactly (skill, not luck)
  defaultRadius: 34, // §5 FIX: retained knob (NOT used by createBall); reserved for §5b
  ai: 9, // §5 FIX: retained knob for a future AI-specific scatter; not in the clean path
} as const;

/** Margin (px) past the green within which a landed ball still rolls on slope. Prototype `tg.r+18`. */
// §5 FIX: unchanged (18px). This only gates WHETHER a near-green landing rolls; it
// is not part of the clean-shot displacement budget (a pin-aimed shot lands well
// inside the green, so it always rolls). Kept at the prototype value.
export const ROLL_GREEN_MARGIN = 18; // §5 FIX: unchanged; gating margin, not a displacement term

/** Off-board margin (px): beyond this the ball is a lost ball (+2). Prototype `±40`. */
export const OUT_OF_BOUNDS_MARGIN = 40;

/** How long a ball LINGERS on screen after coming to rest before it's removed (s). */
export const BALL_REST_SECONDS = 3;
/** How long a ball takes to SPLASH + sink when it comes to rest in water (s). */
export const BALL_SINK_SECONDS = 1.2;

// ---------------------------------------------------------------------------
// Scoring zones (§3.5) — UNSTABLE §5 / §5b
// ---------------------------------------------------------------------------

/**
 * Golf scoring radii relative to the pin (`dp`) and green centre (`dg`) (§3.5).
 * §5b distribution target: the SKILLED-player Monte-Carlo (tests/harness/model.ts:
 * aims uphill to counter the break + counters club curve, Gaussian σ≈0.05 timing
 * on both meters) must land Birdie 45–60%, Par 30–45%, Eagle 3–8%, Ace 1–2%,
 * bogey+ small, avg ≈ −0.3..−0.4 strokes/shot; a sloppy player (σ≈0.15, ignores
 * the break) hovers ≥ ~par. The harness (`node tests/harness/mc.mjs`) is the
 * iteration tool; `tests/unit/distribution.test.ts` is the CI regression guard.
 *
 * These four radii are the §5b levers. With green radius r = min(440·0.16, 72) =
 * 70.4px the rings are: ace 4px, eagle 9.86px (r·0.14), birdie 36.6px (r·0.52);
 * par = on green (dg < r), bogey = dg < r + 24. They nest ace ⊂ eagle ⊂ birdie ⊂
 * green and pass the §5 perfect-shot gate (a calibrated perfect shot lands inside
 * birdieR on average slope). The factors are KEPT at the prototype/post-§5-fix
 * values pending a verified Monte-Carlo run (see HANDOFF note: the §5b tuning loop
 * needs `node` to self-verify; do not change a factor without re-running the
 * harness + the distribution gate and confirming every band + the avg hold).
 *
 * Prototype: `aceR = 4`, `eagleR = r*0.14`, `birdieR = r*0.52`, par `dg < r`,
 * bogey `dg < r + 24`, else double.
 */
export const SCORING = {
  /** Ace: `dp < aceRadius`. Absolute px (container-relative, shape-independent). */
  aceRadius: 2.5, // §5b — kept (≈1.8% ace, band 1–2%); pin ring, unaffected by the bigger container
  /**
   * Eagle: `dp < radius * eagleRadiusFactor`. With the bigger container (radius 88)
   * the old 0.075 factor grew to 6.6px ⇒ ≈10% eagle (over band); re-tuned DOWN so the
   * absolute ring (~5.3px) and the eagle rate (≈6%) return to band.
   */
  eagleRadiusFactor: 0.065, // §5b — grown slightly (was 0.06); offset by tighter swing bands
  /**
   * Birdie: `dp < radius * birdieRadiusFactor`. Old 0.32 grew to 28.2px on radius 88
   * ⇒ birdie too high / par starved. Re-tuned DOWN (ring ≈22.4px) so birdie ≈50% and
   * par recovers into band.
   */
  birdieRadiusFactor: 0.265, // §5b — grown slightly (was 0.255); offset by tighter swing bands
  /** Par: inside the green SHAPE. Bogey: inside the shape expanded by bogeyMargin (px). */
  bogeyMargin: 24, // §5b — kept (24px); bogey vs double split for off-shape rests
  /**
   * Pin-ring reference radius (B geometry). The eagle/birdie rings key off THIS fixed
   * value, NOT the (now DEEP) green container radius — so making the green deeper for
   * POWER-gating distance variation does not inflate the close-to-pin rings. = the old
   * container radius (88) so the §5b ring sizes are unchanged.
   */
  pinRefRadius: 88,
} as const;

/** Stroke delta per result (§3.5). Lowest total wins. Authoritative — also re-exported by scoring.ts. */
export const STROKE_DELTA = {
  ace: -5,
  eagle: -3,
  birdie: -1,
  par: 0,
  bogey: 1,
  double: 2,
} as const;

// ---------------------------------------------------------------------------
// Defensive cards (§3.7)
// ---------------------------------------------------------------------------

/** Plain-data shape for the defensive-card table; mirrors {@link DefenseCard}. */
export interface DefenseCardDef {
  readonly id: string;
  readonly name: string;
  readonly cost: number;
  readonly rarity: Rarity;
  readonly hazard?: 'sand' | 'water' | 'tree';
  readonly radius?: number;
  readonly special?: 'rough' | 'wind' | 'gopher';
}

/** The §3.7 defensive-card table, verbatim from the prototype `DEFENSES` array, plus the Tree
 *  (the SHAPE/FLIGHT counter — see {@link TREE} / {@link clearsTree}). */
export const DEFENSE_CARD_DEFS: readonly DefenseCardDef[] = [
  { id: 'bunker', name: 'Bunker', cost: 2, rarity: 'common', hazard: 'sand', radius: 30 },
  { id: 'pond', name: 'Water', cost: 3, rarity: 'rare', hazard: 'water', radius: 38 },
  { id: 'moat', name: 'Moat', cost: 5, rarity: 'epic', hazard: 'water', radius: 52 },
  { id: 'rough', name: 'Rough', cost: 4, rarity: 'rare', special: 'rough' },
  { id: 'wind', name: 'Wind', cost: 3, rarity: 'rare', special: 'wind' },
  { id: 'timber', name: 'Tree', cost: 4, rarity: 'rare', hazard: 'tree', radius: TREE.radius },
  // Legendary: steals the opponent's next shot for a forced "double par" — expensive on purpose.
  { id: 'gopher', name: 'The Gopher', cost: 8, rarity: 'legend', special: 'gopher' },
] as const;

/**
 * THE GOPHER (legendary defense, §3.7). Playing it arms a steal on the OPPONENT's NEXT shot: the
 * ball flies and lands as normal, then a gopher scampers in, snatches it (insinuating a drop) and
 * laughs — the shot is scored a forced "double par" (a flat penalty, worse than a double bogey),
 * negating even a would-be ace. Costs a lot of energy + a defense charge, so it's an occasional
 * power play. Timings drive the steal animation; `lingerSeconds` = `runSeconds` + `laughSeconds`.
 */
export const GOPHER = {
  /** Flat strokes added to the gopher'd shot ("double par" — worse than a double bogey's +2). */
  penaltyStrokes: 3,
  /** On-screen life of a gopher'd ball AFTER it settles — the snatch + laugh + dive. The gopher
   *  scampers in DURING the ball's flight (so it arrives as the ball lands), so this is just the
   *  steal beat, not a run-in. */
  lingerSeconds: 1.7,
} as const;

/** Base hazard radii by kind (px) — the card table above (§3.7). The PLACED radius
 *  is this scaled by a per-hazard procedural size factor (DEFENSE.hazardSize*). */
export const HAZARD_RADIUS = {
  bunker: 30,
  water: 38,
  moat: 52,
} as const;

export const DEFENSE = {
  /** Rough debuff duration (s). Prototype `ROUGH_SECONDS = 15`. */
  roughSeconds: 15,
  /** Hazard lifetime (s): a bunker/water/moat clears after this long (playtest). */
  hazardSeconds: 15,
  /** Wind gust duration (s). Prototype `WIND_SECONDS = 6`. */
  windSeconds: 6,
  /** Per-frame wind push on balls in flight. Prototype `WIND_FORCE = 0.30`. */
  windForce: 0.3,
  /** Rough box power multiplier (player). Prototype `power *= 0.82`. */
  roughPowerMultiplier: 0.82,
  /** Extra angular spread (degrees) from a rough box (player). Prototype `baseWobble += 5`. */
  roughSpreadDegrees: 5,
  /** Max retries to place a hazard clear of every manicured surface. Prototype loop `tries < 24`. */
  hazardPlacementTries: 24,
  /** Gap (px) from a feature's outline to the hazard disc when ringing it in the rough. */
  hazardRoughGap: 12,
  /** Extra random outward band (px) past the gap, for ring-placement variety. */
  hazardRoughBand: 20,
  /** Per-hazard procedural SIZE: placed radius = base × uniform[sizeMin, sizeMax]. */
  hazardSizeMin: 0.85,
  hazardSizeMax: 1.45,
  /** Angular spread (radians) a TREE is jittered around its FRONT bearing when placed, so it sits
   *  on/near the incoming shot line without always landing dead-centre. ±half this each side. */
  treeFrontArc: 1.4,
  /** Chance a defensive hazard rings the OPPONENT's fairway instead of our own GREEN (§3.7). */
  fairwayHazardChance: 0.35,
  /** Hazard keep-out: never inside `radius * eagleKeepoutFactor` of the pin. Prototype `gr.r*0.12`. */
  eagleKeepoutFactor: 0.12,
  /** Cap on simultaneous hazards a side may hold (AI guard). Prototype `hazards.length < 6`. */
  maxHazardsPerGreen: 6,
} as const;

// ---------------------------------------------------------------------------
// AI opponent (§3.8) — difficulty is a parameter; Normal is the default (decided)
// ---------------------------------------------------------------------------

/** Per-difficulty AI parameters. Kept swappable so a remote opponent can replace it (§4.5). */
export interface AiParams {
  /** Energy regen per second for the AI side. Prototype `aiEnergy += dt*0.6`. */
  readonly energyRegenPerSecond: number;
  /** Attack-timer reset window: `attackCooldownMin + r * attackCooldownRange` (s). Prototype `3.5 + r*3.0`. */
  readonly attackCooldownMin: number;
  readonly attackCooldownRange: number;
  /** Defense-timer reset window: `defenseCooldownMin + r * defenseCooldownRange` (s). Prototype `6.0 + r*4.0`. */
  readonly defenseCooldownMin: number;
  readonly defenseCooldownRange: number;
  /** Base power before jitter. Prototype `power = 0.78 + ...`. */
  readonly powerBase: number;
  /** Symmetric power jitter amplitude: `(r-0.5) * powerJitter`. Prototype `*0.22`. */
  readonly powerJitter: number;
  /** Power clamp after jitter. Prototype `max(0.35, min(1, power))`. */
  readonly powerMin: number;
  readonly powerMax: number;
  /** Aim spread multiplier applied to the club's base spread. Prototype `g.spread*2.0`. */
  readonly spreadMultiplier: number;
  /** Probability per attack tick of skipping ("hesitation"). Prototype `Math.random() > 0.2` → 0.2 skip. */
  readonly skipChance: number;
  /** Probability of a gross shank. Prototype `Math.random() < 0.3`. */
  readonly shankChance: number;
  /** Shank magnitude (radians, symmetric): `(r-0.5) * shankMagnitude`. Prototype `*0.30`. */
  readonly shankMagnitude: number;
  /** Rough box: power multiplier + extra spread (degrees) for the AI. Prototype `*0.80`, `+6`. */
  readonly roughPowerMultiplier: number;
  readonly roughSpreadDegrees: number;
}

/**
 * Difficulty table (§3.8). Easy/Normal/Hard scale the same policy; the AI was
 * "tuned down once already" so Normal mirrors the prototype's (already softened)
 * numbers. Easy is weaker (more hesitation, more shank, more jitter); Hard is
 * sharper. These are feel knobs and may be re-tuned alongside §5b.
 */
export const AI_PARAMS: Readonly<Record<Difficulty, AiParams>> = {
  easy: {
    energyRegenPerSecond: 0.45,
    attackCooldownMin: 4.5,
    attackCooldownRange: 4.0,
    defenseCooldownMin: 8.0,
    defenseCooldownRange: 5.0,
    powerBase: 0.78,
    powerJitter: 0.34,
    powerMin: 0.35,
    powerMax: 1.0,
    spreadMultiplier: 3.0,
    skipChance: 0.4,
    shankChance: 0.45,
    shankMagnitude: 0.4,
    roughPowerMultiplier: 0.8,
    roughSpreadDegrees: 6,
  },
  normal: {
    // Prototype values verbatim (§3.8 current AI).
    energyRegenPerSecond: 0.6,
    attackCooldownMin: 3.5,
    attackCooldownRange: 3.0,
    defenseCooldownMin: 6.0,
    defenseCooldownRange: 4.0,
    powerBase: 0.78,
    powerJitter: 0.22,
    powerMin: 0.35,
    powerMax: 1.0,
    spreadMultiplier: 2.0,
    skipChance: 0.2,
    shankChance: 0.3,
    shankMagnitude: 0.3,
    roughPowerMultiplier: 0.8,
    roughSpreadDegrees: 6,
  },
  hard: {
    energyRegenPerSecond: 0.75,
    attackCooldownMin: 2.5,
    attackCooldownRange: 2.0,
    defenseCooldownMin: 4.5,
    defenseCooldownRange: 3.0,
    powerBase: 0.78,
    powerJitter: 0.12,
    powerMin: 0.4,
    powerMax: 1.0,
    spreadMultiplier: 1.3,
    skipChance: 0.08,
    shankChance: 0.15,
    shankMagnitude: 0.2,
    roughPowerMultiplier: 0.85,
    roughSpreadDegrees: 5,
  },
} as const;

/** Default AI difficulty — human-decided (§3.8): Normal. */
export const DEFAULT_DIFFICULTY: Difficulty = 'normal';

/** Initial AI timers at match start. Prototype `aiAtkTimer:3.0, aiDefTimer:5.0`. */
export const AI_INITIAL = {
  attackTimer: 3.0,
  defenseTimer: 5.0,
  /** Prototype seeds the AI with `aiEnergy:4`. */
  startEnergy: 4,
} as const;

// ---------------------------------------------------------------------------
// Anti-turtle economy (playtest) — keep a leader from freezing a lead and stalling
// ---------------------------------------------------------------------------

/**
 * SHOT CLOCK (playtest): a leader can't bank a few good shots then stop attacking and run out
 * the clock on pure defense. Each side has a shot clock that counts down ONLY while it can
 * afford a club (you're never penalised for being broke) and the match clock is still running;
 * letting it hit zero costs a {@link penaltyStroke} and resets it. Taking an offensive shot
 * resets it — so idling = bleeding strokes. Symmetric (the AI attacks well inside it).
 */
export const SHOT_CLOCK = {
  /** Idle-attack limit (s). Exceed it while able to attack ⇒ a penalty stroke. */
  seconds: 25,
  /** Strokes added to a side's score each time its shot clock expires. */
  penaltyStroke: 1,
} as const;

/**
 * DEFENSE CHARGES (playtest): defense is no longer free denial — it's fuelled by AGGRESSION.
 * Each offensive shot banks one charge (capped); each defensive card spends one. A pure turtle
 * earns no charges, so it can't lock the opponent down — it has to keep attacking to keep
 * defending. The two anti-turtle levers reinforce: the shot clock sets a minimum attack cadence,
 * which is exactly what refills the charges that gate defense.
 */
export const DEFENSE_CHARGE = {
  /** Opening reserve so an early defensive play is possible before your first shot. */
  start: 1,
  /** Cap — you can't hoard a wall of defense; banked charges are spent down then re-earned. */
  max: 2,
  /** Charges earned per offensive shot fired. */
  perShot: 1,
} as const;

// ---------------------------------------------------------------------------
// Hands / decks (§3.3 / §3.6)
// ---------------------------------------------------------------------------

/** Cards held in each hand row (offense + defense). Prototype deals 3 each. */
export const HAND_SIZE = 3;
/** Copies of each card shuffled into a deck. Prototype `[...GOLFERS,...GOLFERS,...GOLFERS]`. */
export const DECK_COPIES = 3;

// ---------------------------------------------------------------------------
// Tie-break (§3.5) — human-decided: sudden-death playoff
// ---------------------------------------------------------------------------

/**
 * Tie-break rule (human-decided, §3.5): on a tied final score, both players take
 * one more "playoff" shot and the one whose ball comes to rest closest to the pin
 * (smallest `dp`) wins. {@link TieBreakRule} captures the parameters.
 */
export interface TieBreakRule {
  /** Discriminant for the resolution strategy. */
  readonly kind: 'sudden-death-closest-to-pin';
  /** Each side takes exactly this many extra shots in the playoff. */
  readonly shotsPerSide: number;
  /** Metric compared between sides; smaller wins. `dp` = distance to pin. */
  readonly metric: 'dp';
}

export const TIE_BREAK: TieBreakRule = {
  kind: 'sudden-death-closest-to-pin',
  shotsPerSide: 1,
  metric: 'dp',
};
